import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Select, {
  components,
  SingleValue,
  StylesConfig,
} from "react-select";

interface OptionType {
  value: string | number;
  label: string;
}

interface SelectGroupProps {
  controlId: string;
  label: string;
  icon?: string;
  options?: OptionType[];
  valueType?: OptionType | null;
  onChange?: (selectedOption: OptionType | null) => void;
  handleCategoryChange?: (selectedOption: OptionType | null) => void;
  placeholder?: string;
  noOptionsMessage?: string;
  name?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
}

const SelectGroup: React.FC<SelectGroupProps> = ({
  controlId,
  label,
  icon = "list",
  options = [],
  valueType = null,
  onChange,
  handleCategoryChange,
  placeholder = "Select an option",
  noOptionsMessage,
  name,
  isSearchable = true,
  isClearable = true,
  disabled = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  const safeOptions = useMemo<OptionType[]>(() => {
    if (!Array.isArray(options)) return [];
    return options.filter(
      (opt) =>
        opt &&
        typeof opt === "object" &&
        opt.value !== undefined &&
        opt.label !== undefined,
    );
  }, [options]);

  useEffect(() => {
    if (
      !valueType ||
      typeof valueType !== "object" ||
      valueType.value === undefined ||
      valueType.value === null
    ) {
      setSelectedOption(null);
      return;
    }

    const found =
      safeOptions.find(
        (opt) => String(opt.value) === String(valueType.value),
      ) || null;

    setSelectedOption(found || valueType);
  }, [valueType, safeOptions]);

  const DropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {icon ? (
          <i className={`las la-${icon}`} style={{ marginRight: "8px" }} />
        ) : null}
        {props.children}
      </div>
    </components.DropdownIndicator>
  );

  const resolvedNoOptionsMessage = useMemo(() => {
    return (obj: { inputValue: string }) =>
      noOptionsMessage
        ? noOptionsMessage.replace("{inputValue}", obj.inputValue)
        : `Aucune option pour ${obj.inputValue}`;
  }, [noOptionsMessage]);

  const handleSelectChange = (option: SingleValue<OptionType>) => {
    const opt = option ?? null;

    const canonical = opt
      ? safeOptions.find((o) => String(o.value) === String(opt.value)) || opt
      : null;

    setSelectedOption(canonical);

    if (onChange) {
      onChange(canonical);
      return;
    }

    if (handleCategoryChange) {
      handleCategoryChange(canonical);
    }
  };

  const customStyles: StylesConfig<OptionType, false> = {
    menu: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (base) => ({ ...base, color: "#000" }),
    placeholder: (base) => ({ ...base, color: "#6b7280" }),
    control: (base) => ({
      ...base,
      minHeight: "42px",
      borderRadius: "10px",
    }),
  };

  return (
    <Form.Group controlId={controlId} as={Row} style={{ marginTop: "15px" }}>
      <Form.Label column sm="4" md="4" lg="4" className="text-md-end text-left">
        {label}
      </Form.Label>

      <Col sm="8" md="8" lg="8">
        <Select<OptionType, false>
          inputId={controlId}
          name={name || controlId}
          classNamePrefix="react-select"
          className="w-100"
          options={safeOptions}
          value={selectedOption}
          onChange={handleSelectChange}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => String(opt.value)}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={disabled}
          components={{ DropdownIndicator }}
          placeholder={placeholder}
          noOptionsMessage={resolvedNoOptionsMessage}
          styles={customStyles}
        />
      </Col>
    </Form.Group>
  );
};

export default SelectGroup;